import { KeyboardEvent } from 'react'
import cx from 'classnames'

import { ExpandingTextArea } from 'common/ExpandingTextArea/ExpandingTextArea'

import Styles from './ListInput.module.scss'

interface Props {
  className?: string
  maxLength?: number
  name: string
  onChange: (value: Props['value']) => void
  placeholder?: string
  value: string[] | null
}

export const ListInput = ({ className, maxLength, name, onChange, placeholder, value }: Props) => {
  if (!(value && value.length)) {
    onChange([''])
  }

  const handleChange = (index: number, inputValue: string) => {
    if (!value) {
      return
    }

    const newValue = value.slice()
    newValue[index] = inputValue

    if (!maxLength || newValue.reduce((length, text) => length + text.length, 0) <= maxLength) {
      onChange(newValue)
    }
  }

  const addItem = (index: number, selectionStart: number) => {
    if (!value) {
      return
    }

    const newValue = value.slice()
    const existingText = newValue[index]
    newValue[index] = existingText.substring(0, selectionStart)
    const splitText = existingText.substring(selectionStart)

    newValue.splice(index + 1, 0, splitText)

    onChange(newValue)
  }

  const focusNextInput = (element: HTMLElement) => {
    const parentListItem = element.closest('li')

    if (parentListItem) {
      const nextListItem = parentListItem.nextSibling as HTMLElement

      if (nextListItem) {
        const nextInput = nextListItem.querySelector('textarea')

        if (nextInput) {
          nextInput.focus()
          nextInput.selectionStart = nextInput.selectionEnd = 0
        }
      }
    }
  }

  const focusPreviousInput = (element: HTMLElement, selectionStart?: number) => {
    const parentListItem = element.closest('li')

    if (parentListItem) {
      const previousListItem = parentListItem.previousSibling as HTMLElement

      if (previousListItem) {
        const previousInput = previousListItem.querySelector('textarea')

        if (previousInput) {
          previousInput.focus()
          setTimeout(
            () =>
              (previousInput.selectionStart = previousInput.selectionEnd = selectionStart
                ? selectionStart
                : previousInput.value.length),
            0,
          )
        }
      }
    }
  }

  const removeItem = (index: number) => {
    if (!value) {
      return
    }

    const newValue = value.slice()

    // NOTE: append text to previous text
    if (index > 0) {
      newValue[index - 1] = newValue[index - 1] + newValue[index]
    }
    newValue.splice(index, 1)

    onChange(newValue)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>, index: number) => {
    if (!value) {
      return
    }

    // NOTE: if there is a selection we don't handle anything
    if (event.currentTarget.selectionStart !== event.currentTarget.selectionEnd) {
      return
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      addItem(index, event.currentTarget.selectionStart)

      const currentInput = event.currentTarget
      setTimeout(() => focusNextInput(currentInput), 0)
      event.preventDefault()
    }

    // NOTE: only if the cursor is at the beginning of an input and backspace is pressed
    if (event.key === 'Backspace' && !event.currentTarget.selectionStart && index > 0) {
      removeItem(index)

      const currentInput = event.currentTarget

      focusPreviousInput(currentInput, value[index - 1].length)
      event.preventDefault()
    }

    if (event.key === 'ArrowUp') {
      const firstLine = event.currentTarget.value.split(/\n/)[0]

      // NOTE: if cursor is on the first line
      if (!firstLine || event.currentTarget.selectionStart <= firstLine.length) {
        focusPreviousInput(event.currentTarget)
        event.preventDefault()
      }
    }

    if (event.key === 'ArrowDown') {
      const lines = event.currentTarget.value.split(/\n/)
      const linesUntilLastLine = lines.slice(0, lines.length - 1)
      const characterCountUntilLastLine = linesUntilLastLine.reduce((count, line) => count + line.length, 0)

      // NOTE: if cursor is on the last line
      if (event.currentTarget.selectionStart > characterCountUntilLastLine + 1 || !event.currentTarget.value) {
        focusNextInput(event.currentTarget)
        event.preventDefault()
      }
    }
  }

  return (
    <ul className={cx(Styles.listInput, className)}>
      {value &&
        value.map((text, index) => (
          <li key={index}>
            <ExpandingTextArea
              autoComplete="off"
              name={`${name}-${index}`}
              onChange={event => handleChange(index, event.currentTarget.value)}
              onKeyDown={event => onKeyDown(event, index)}
              placeholder={placeholder}
              value={text}
            />
          </li>
        ))}
    </ul>
  )
}
